import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ScrollText, Swords, Map, BookOpen, Crown, Lock } from 'lucide-react';
import { useAppState } from '../hooks/useAppState';
import { PixelCrest } from '../components/shared/PixelCrest';
import { SkinPicker } from '../components/guild/SkinPicker';
import { HomeRows } from '../components/guild/HomeRows';
import { RuleChangesNotice, useRuleChanges } from '../components/guild/RuleChanges';
import { authApi } from '../lib/api';
import { INK, PANEL, RIM, ICE, TEAL, TITLE_FONT, PX_FONT, pixelFrame, arcadeTitleShadow, ACCENT, GOLD, PAGE, CASE, ON_ACCENT, TITLE_WEIGHT, HEAD_SHADOW, SOFT_TEXT, PANEL_GLASS, FIELD, FIELD_SHADOW } from '../components/guild/theme';
import { useSkinCopy } from '../hooks/useSkinCopy';

type Colors6 = [string, string, string, string, string, string];
const CREST_COLORS: Colors6 = ['#B9DCE8', '#9EC6D8', '#DAEEF4', '#92BCD3', '#7E9DAD', '#BADBE6'];

interface Door {
  key: string;
  name: string;
  blurb: string;
  icon: ReactNode;
  to?: string;
  tag?: string;
  locked?: string;
}

const DOORS: Door[] = [
  { key: 'rx', name: 'Prescription Engine', blurb: 'Your risk numbers, fitted to your firm and your stats.', icon: <ScrollText size={22} />, to: '/assess' },
  { key: 'tracker', name: 'Trade Tracker', blurb: 'Log backtest, paper and live trades. Your prescription re-fits as you go.', icon: <Swords size={22} />, to: '/tracker', tag: 'New' },
  { key: 'campaign', name: 'Campaign', blurb: 'Your progression map and the four gates to the next tier.', icon: <Map size={22} />, to: '/dashboard' },
  { key: 'resources', name: 'Resources', blurb: 'The playbooks, models and explainers behind every number.', icon: <BookOpen size={22} />, to: '/resources' },
  { key: 'mentor', name: 'Mentor Hall', blurb: 'Cohort stats, trader audits, top-vs-bottom expectancy.', icon: <Crown size={22} />, locked: 'Mentors only' },
];

function DoorTile({ door, onOpen }: { door: Door; onOpen: () => void }) {
  const [hot, setHot] = useState(false);
  const locked = Boolean(door.locked);
  return (
    <button
      type="button"
      onClick={locked ? undefined : onOpen}
      onMouseEnter={() => setHot(true)}
      onMouseLeave={() => setHot(false)}
      onFocus={() => setHot(true)}
      onBlur={() => setHot(false)}
      aria-disabled={locked}
      style={{
        position: 'relative',
        textAlign: 'left',
        background: PANEL_GLASS,
        border: 'none',
        padding: '16px 16px 14px',
        cursor: locked ? 'not-allowed' : 'pointer',
        boxShadow: pixelFrame(hot && !locked),
        transform: hot && !locked ? 'translateY(-3px)' : 'none',
        transition: 'transform 0.15s steps(3), box-shadow 0.15s steps(3)',
        opacity: locked ? 0.62 : 1,
        minHeight: 150,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{
          width: 42, height: 42, display: 'grid', placeItems: 'center',
          background: FIELD, color: ICE,
          boxShadow: FIELD_SHADOW,
          filter: `drop-shadow(0 3px 0 ${INK})`,
        }}>
          {door.icon}
        </div>
        {door.tag && (
          <span style={{
            fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.14em', textTransform: CASE,
            color: ON_ACCENT, background: ACCENT, padding: '3px 7px',
            boxShadow: `0 0 0 2px ${INK}`,
          }}>
            {door.tag}
          </span>
        )}
      </div>
      <div style={{
        fontFamily: TITLE_FONT, fontSize: 18, color: ICE, letterSpacing: '0.04em',
        textShadow: HEAD_SHADOW,
      }}>
        {door.name}
      </div>
      <div style={{ fontFamily: PX_FONT, fontSize: 10, lineHeight: 1.55, letterSpacing: '0.06em', color: SOFT_TEXT }}>
        {door.blurb}
      </div>
      {locked && (
        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.16em', textTransform: CASE, color: TEAL }}>
          <Lock size={12} /> {door.locked}
        </div>
      )}
    </button>
  );
}

/* Positions below are % of the 384x216 source image; the stage keeps that
   aspect while covering the viewport, so overlays stay pinned to the art */
const LANTERNS = [
  { x: 10.4, y: 28.7, r: 5, delay: 0 },
  { x: 19.8, y: 34.3, r: 4, delay: 0.6 },
  { x: 33, y: 43, r: 3, delay: 1.4 },
  { x: 67.2, y: 43, r: 3, delay: 0.9 },
  { x: 80.7, y: 34.3, r: 4, delay: 1.1 },
  { x: 90.4, y: 28.7, r: 5, delay: 0.3 },
];
const HEARTH = { x: 50.3, y: 57.9 };
/* Hearth banners redrawn in source-pixel units (384x216) so they hang from the
   same rail height as the wall banners */
const HEARTH_BANNERS = [163, 224];
const BANNER_TOP = 24;
const BANNER_BOTTOM = 122;
const BANNER_W = 18;

/* Inevitrade IT mark rasterized onto a pixel grid (one cell = one
   half-pixel of the 384-wide plate), fine enough to read as the logo but still stitched */
const IT_POLY: [number, number][] = [[14.01, 11.97], [16.92, 0], [7.47, 0], [0, 30.67], [9.46, 30.67], [12.06, 19.97], [18.89, 19.97], [13.37, 42.64], [22.83, 42.64], [28.35, 19.97], [36.57, 19.97], [38.52, 11.97]];
const IT_COLS = 16;
const IT_ROWS = 18;
function inPoly(x: number, y: number, poly: [number, number][]) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
const IT_CELLS: [number, number][] = [];
for (let r = 0; r < IT_ROWS; r++) {
  for (let c = 0; c < IT_COLS; c++) {
    if (inPoly(((c + 0.5) / IT_COLS) * 38.52, ((r + 0.5) / IT_ROWS) * 42.64, IT_POLY)) IT_CELLS.push([c, r]);
  }
}

function HallBackdrop() {
  const embers = useMemo(() => Array.from({ length: 18 }, () => ({
    x: HEARTH.x - 4 + Math.random() * 8,
    size: Math.random() < 0.3 ? 6 : 4,
    dur: 3.5 + Math.random() * 3,
    delay: -Math.random() * 6,
    drift: -30 + Math.random() * 60,
  })), []);
  const motes = useMemo(() => Array.from({ length: 22 }, () => ({
    x: Math.random() * 100,
    y: 10 + Math.random() * 60,
    dur: 14 + Math.random() * 12,
    delay: -Math.random() * 20,
  })), []);

  return (
    <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', display: 'var(--pal-backdrop)' }}>
      <style>{`
        @keyframes hall-push { from { transform: translate(-50%, -50%) scale(1); } to { transform: translate(-50%, -50%) scale(1.06); } }
        @keyframes flicker {
          0%, 100% { opacity: 0.6; } 15% { opacity: 0.85; } 30% { opacity: 0.55; }
          50% { opacity: 0.9; } 65% { opacity: 0.62; } 80% { opacity: 0.8; }
        }
        @keyframes ember-rise {
          0% { transform: translate(0, 0); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translate(var(--drift), -38vh); opacity: 0; }
        }
        @keyframes mote-drift {
          0% { transform: translate(0, 0); opacity: 0; }
          20% { opacity: 0.7; } 80% { opacity: 0.7; }
          100% { transform: translate(40px, 60px); opacity: 0; }
        }
      `}</style>
      <div
        style={{
          position: 'absolute', left: '50%', top: '50%',
          width: 'max(100vw, 177.78vh)', height: 'max(56.25vw, 100vh)',
          transformOrigin: '50% 62%',
          animation: 'hall-push 40s ease-in-out infinite alternate',
        }}
      >
        {/* The art ships with thin letterbox bars; scale the whole plate (and its overlays) past them */}
        <div style={{ position: 'absolute', inset: 0, transform: 'scale(1.11)' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(/assets/crest/guild-hall-004-hd.webp)', backgroundSize: '100% 100%' }} />

        {/* Hearth banners + stitched Inevitrade IT mark */}
        <svg viewBox="0 0 384 216" preserveAspectRatio="none" shapeRendering="crispEdges" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          {HEARTH_BANNERS.map(cx => {
            const x0 = cx - BANNER_W / 2;
            const cell = 0.75;
            const mx = cx - (IT_COLS * cell) / 2;
            const my = BANNER_BOTTOM - 38;
            const towardFire = cx < 192 ? x0 + BANNER_W - 3 : x0;
            return (
              <g key={cx}>
                <rect x={x0 - 3} y={BANNER_TOP - 3} width={BANNER_W + 6} height={2} fill="#2A1E18" />
                <rect x={x0 - 4} y={BANNER_TOP - 4} width={2} height={4} fill="#4A3526" />
                <rect x={x0 + BANNER_W + 2} y={BANNER_TOP - 4} width={2} height={4} fill="#4A3526" />
                <polygon
                  points={`${x0},${BANNER_TOP - 1} ${x0 + BANNER_W},${BANNER_TOP - 1} ${x0 + BANNER_W},${BANNER_BOTTOM + 8} ${cx},${BANNER_BOTTOM} ${x0},${BANNER_BOTTOM + 8}`}
                  fill="#6E141A"
                />
                <rect x={cx < 192 ? x0 : x0 + BANNER_W - 2} y={BANNER_TOP - 1} width={2} height={BANNER_BOTTOM - BANNER_TOP + 6} fill="#4A0C12" />
                <rect x={towardFire} y={BANNER_TOP - 1} width={3} height={BANNER_BOTTOM - BANNER_TOP + 6} fill="#8C1E24" opacity={0.8} />
                <rect x={x0} y={BANNER_TOP + 3} width={BANNER_W} height={1} fill="#C8963A" />
                <rect x={x0} y={BANNER_BOTTOM - 4} width={BANNER_W} height={1} fill="#C8963A" />
                {IT_CELLS.map(([c, r]) => <rect key={`s${c}-${r}`} x={mx + c * cell} y={my + (r + 1) * cell} width={cell} height={cell} fill="#4A0E10" />)}
                {IT_CELLS.map(([c, r]) => <rect key={`f${c}-${r}`} x={mx + c * cell} y={my + r * cell} width={cell} height={cell} fill={r < 5 ? GOLD : r < 12 ? '#D9A441' : '#B07A2A'} />)}
              </g>
            );
          })}
        </svg>

        {/* Hearth fire: pulsing core + broad warm spill */}
        <div style={{
          position: 'absolute', left: `${HEARTH.x}%`, top: `${HEARTH.y}%`, width: '9%', height: '12%',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(ellipse at 50% 70%, rgba(255,200,90,0.9) 0%, rgba(255,120,40,0.5) 40%, transparent 72%)',
          mixBlendMode: 'screen', animation: 'flicker 0.9s steps(4) infinite',
        }} />
        <div style={{
          position: 'absolute', left: `${HEARTH.x}%`, top: `${HEARTH.y + 6}%`, width: '46%', height: '40%',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(ellipse, rgba(255,140,50,0.22) 0%, transparent 70%)',
          mixBlendMode: 'screen', animation: 'flicker 2.6s steps(6) infinite',
        }} />

        {LANTERNS.map(l => (
          <div key={l.x} style={{
            position: 'absolute', left: `${l.x}%`, top: `${l.y}%`, width: `${l.r}%`, height: `${l.r * 1.78}%`,
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, rgba(255,190,90,0.55) 0%, rgba(255,140,50,0.18) 45%, transparent 70%)',
            mixBlendMode: 'screen', animation: `flicker 1.7s steps(5) ${l.delay}s infinite`,
          }} />
        ))}

        {embers.map((e, i) => (
          <span key={i} style={{
            position: 'absolute', left: `${e.x}%`, top: `${HEARTH.y}%`, width: e.size, height: e.size,
            background: e.size > 4 ? '#FFB347' : '#FF7A2E',
            boxShadow: '0 0 6px rgba(255,140,50,0.8)',
            ['--drift' as string]: `${e.drift}px`,
            animation: `ember-rise ${e.dur}s linear ${e.delay}s infinite`,
          }} />
        ))}

        {motes.map((m, i) => (
          <span key={i} style={{
            position: 'absolute', left: `${m.x}%`, top: `${m.y}%`, width: 3, height: 3,
            background: 'rgba(190,235,245,0.8)',
            animation: `mote-drift ${m.dur}s linear ${m.delay}s infinite`,
          }} />
        ))}
        </div>
      </div>

      {/* Darken toward the content so the doors stay readable */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(5,26,34,0.85) 0%, rgba(5,26,34,0.55) 22%, rgba(5,26,34,0.05) 42%, rgba(5,26,34,0.05) 78%, rgba(5,26,34,0.6) 100%)' }} />
    </div>
  );
}

export function Hub() {
  const navigate = useNavigate();
  const copy = useSkinCopy();
  const { user } = useAppState();
  const name = user?.discordUsername ?? 'traveler';
  // Admins (and mods) are told when prop-firm rules change
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => { authApi.me(user?.discordUsername || 'trader').then(me => setIsAdmin(me.roles.includes('admin'))).catch(() => undefined); }, [user?.discordUsername]);
  const ruleChanges = useRuleChanges(user?.discordUsername || 'trader', isAdmin);

  return (
    <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', background: PAGE }}>

      <main style={{ position: 'relative', zIndex: 1, maxWidth: 1180, margin: '0 auto', padding: '40px 24px 32px', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <header className="hub-header" style={{ display: 'flex', alignItems: 'center', gap: 22, marginBottom: 32, flexWrap: 'wrap' }}>
          <PixelCrest size={84} colors={CREST_COLORS} />
          <div>
            <h1 className="pal-title" style={{
              margin: 0, fontWeight: TITLE_WEIGHT, fontFamily: TITLE_FONT, fontSize: 'clamp(34px, 6vw, 52px)', letterSpacing: '0.1em',
              color: ICE, lineHeight: 1.05,
              textShadow: arcadeTitleShadow,
            }}>
              {copy.hub[0]} <span className="pal-em">{copy.hub[1]}</span>
            </h1>
            <p style={{ margin: '10px 0 0', fontFamily: PX_FONT, fontSize: 12, letterSpacing: '0.16em', textTransform: CASE, color: TEAL }}>
              Welcome back, {name}. Where to first?
            </p>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* Moderator shortcuts */}
            {isAdmin && (
              <span style={{ display: 'flex', gap: 12, fontFamily: PX_FONT, fontSize: 11, letterSpacing: '0.08em' }}>
                <a href="/admin/firms" style={{ color: TEAL, textDecoration: 'none' }}>Firm rules</a>
                <a href="/admin/members" style={{ color: TEAL, textDecoration: 'none' }}>Members</a>
              </span>
            )}
            <SkinPicker />
          </div>
        </header>

        <RuleChangesNotice count={ruleChanges.unseen.length} onOpen={() => navigate('/admin/firms?tab=changes')} />
        <HomeRows />
      </main>
    </div>
  );
}
